import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {useDispatch} from 'react-redux';

import {BulkActionProgress} from 'src/components/bulk-tool';
import {BulkActionFooter} from 'src/components/bulk-tool/bulk-action-footer';
import {STACKOVERFLOW_MESSAGE} from 'src/constants';
import {useBackgroundTaskManager} from 'src/contexts/background-task-context';
import {TOOL_PROGRESS_LABEL, TOOL_TASK} from 'src/modules/bulk-update/update-user-group';
import {
    selectUpdateUserGroupTask,
    selectUpdateUserGroupUuid,
    setBackgroundTask,
    setShown,
    setUuid,
} from 'src/modules/bulk-update/update-user-group/update-user-group.slice';
import {
    assignGroupMemberBulk,
    getUserGroupUpdateProgress,
    stopUserGroupBulkUpdate,
} from 'src/openapi/admin/apis/BulkGroupAssignApi';
import {useAppDispatch} from 'src/store/hooks';
import {getError} from 'src/utils/get-error';

import Button from '@pb/kit-component-button';
import {DialogHeader, DialogMain} from '@pb/kit-component-dialog-v2';
import {Group} from '@pb/kit-component-group';
import {Message} from '@pb/kit-component-message';

import {GroupMembersTab, GroupSettingsTab, GroupTabs} from '../components';
import {GROUP_TABS} from '../constants';
import {useGetAccountTeamMembers} from '../hooks/useGetAccountTeamMembers';
import {groupsTabMembersActions} from '../slices/groups-tab-members.slice';
import {groupsTabActions} from '../slices/groups-tab.slice';

import {CREATE_GROUP_REQUEST_MESSAGE, CREATE_GROUP_TASK, TOOL_NAME} from './constants';
import {useCreateGroup} from './useCreateGroup';

import type {UpdateGroupMembersRequest} from '../typedef';
import type {ActionOptions} from 'src/components/save-with-reason/action-with-reason-button';
import type {MemberInfo} from 'src/openapi/admin/models/MemberInfo';


type Props = {
    accountId: number,
    onCloseModal: (submit: boolean) => void,
    onPendingChange?: (pending: boolean) => void,
};

export const CreateGroupModal = ({accountId, onCloseModal, onPendingChange}: Props) => {
    const [createdGroupId, setCreatedGroupId] = useState<number>();
    const [saving, setSaving] = useState(false);
    const setPending = useCallback((pending: boolean) => {
        setSaving(pending);
        onPendingChange?.(pending);
    }, [onPendingChange]);
    const dispatch = useDispatch();
    const apiDispatch = useAppDispatch();

    const createBackgroundTask = useBackgroundTaskManager();
    const [tab, setTab] = useState(GROUP_TABS.SETTINGS.name);

    const {
        isLoading: isAccountTeamMembersLoading,
        loadingError: accountTeamMembersLoadingError,
        data: accountTeamMembers,
    } = useGetAccountTeamMembers({accountId});

    const {
        isLoading: isGroupLoading,
        loadingError: groupLoadingError,
        data: groupData,
        setData: setGroupData,
        saveChanges: saveGroupAction,
    } = useCreateGroup({accountId});

    const [updateMembersRequest, setUpdateMembersRequest] = useState<UpdateGroupMembersRequest>({});
    const isMembersChanged = useMemo(() => !!(
        updateMembersRequest.addMembers?.length || updateMembersRequest.removeMembers?.length
    ), [updateMembersRequest]);

    const refreshTable = useCallback(() => {
        dispatch(groupsTabActions.requireRefresh());
        dispatch(groupsTabMembersActions.requireRefresh());
    }, [dispatch]);

    const createGroup = useCallback(async (options?: ActionOptions) => {
        const backgroundTask = createBackgroundTask(
            CREATE_GROUP_TASK,
            CREATE_GROUP_REQUEST_MESSAGE, {
                maxStack: 1,
                stackOverflowMessage: STACKOVERFLOW_MESSAGE,
            },
        );

        if (backgroundTask) {
            const result = createdGroupId ? {groupId: createdGroupId} : await saveGroupAction();
            if (typeof result === 'string') {
                backgroundTask.completeFailure(result);
                return result;
            } else {
                backgroundTask.completeSuccess(<span>Group <b>{groupData.name}</b> created</span>);

                if (result?.groupId) {
                    setCreatedGroupId(result.groupId);
                    if (isMembersChanged) {
                        const response = await apiDispatch(assignGroupMemberBulk({
                            ...options,
                            updateUserGroup: [
                                ...(updateMembersRequest.addMembers || []).map(user => ({
                                    userId: user.userId,
                                    groupId: result.groupId,
                                })),
                                ...(updateMembersRequest.removeMembers || []).map(user => ({
                                    userId: user.userId,
                                })),
                            ],
                        }));

                        if (!response.payload.success) {
                            return getError({error: response.payload.data, separator: ' '});
                        }
                        if (response.payload.success) {
                            const updateUsersTask = createBackgroundTask(
                                TOOL_TASK,
                                <BulkActionProgress
                                    label={TOOL_PROGRESS_LABEL}
                                    taskSelector={selectUpdateUserGroupTask}
                                    idSelector={selectUpdateUserGroupUuid}
                                    getProgress={id => apiDispatch(getUserGroupUpdateProgress({id}))}
                                    stopAction={id => apiDispatch(stopUserGroupBulkUpdate({id}))}
                                    setShown={setShown}
                                    onDone={refreshTable}
                                />,
                                {
                                    maxStack: 1,
                                    stackOverflowMessage: STACKOVERFLOW_MESSAGE,
                                },
                            );

                            if (updateUsersTask) {
                                dispatch(setUuid(response.payload.data));
                                dispatch(setBackgroundTask(updateUsersTask));
                            }
                        }
                    } else {
                        refreshTable();
                    }
                }
            }
            onCloseModal(true);
        }
    }, [
        apiDispatch,
        createdGroupId,
        createBackgroundTask,
        dispatch,
        refreshTable,
        saveGroupAction,
        onCloseModal,
        groupData,
        isMembersChanged,
        updateMembersRequest,
    ]);

    useEffect(() => {
        if (!createdGroupId && accountTeamMembers.length) {
            const ownerId = accountTeamMembers.find(manager => manager.primary)?.id;

            if (ownerId) {
                setGroupData(prev => ({...prev, ownerId}));
            }
        }
    }, [accountTeamMembers, createdGroupId, setGroupData]);

    const renderedUsers: (MemberInfo & {view: string})[] = useMemo(
        () => updateMembersRequest.addMembers?.map(user => ({...user, view: 'Added'})) || [],
        [updateMembersRequest],
    );

    return (
        <>
            <DialogHeader onClose={() => onCloseModal(false)}>
                {TOOL_NAME}
            </DialogHeader>
            <DialogMain style={{padding: '0 24px 24px'}}>
                <Group direction="v" gap="s">
                    {createdGroupId ? (
                        <Message color="info">
                            Group settings have been saved. Retry assigning members to complete creation.
                        </Message>
                    ) : null}
                    <GroupTabs currentTab={tab} setCurrentTab={setTab} membersCount={renderedUsers.length}/>
                    {tab === GROUP_TABS.SETTINGS.name && (
                        <GroupSettingsTab
                            accountId={accountId}
                            isLoading={isGroupLoading || isAccountTeamMembersLoading}
                            loadingError={groupLoadingError || accountTeamMembersLoadingError}
                            data={groupData}
                            setData={setGroupData}
                            accountTeamMembers={accountTeamMembers}
                            viewType="New"
                            disabled={saving || !!createdGroupId}
                        />
                    )}
                    {tab === GROUP_TABS.MEMBERS.name && (
                        <GroupMembersTab
                            accountId={accountId}
                            isLoading={false}
                            loadingError={null}
                            members={[]}
                            renderedUsers={renderedUsers}
                            requestInfo={updateMembersRequest}
                            setRequestInfo={setUpdateMembersRequest}
                            viewType="New"
                        />
                    )}
                </Group>
            </DialogMain>
            <BulkActionFooter onPendingChange={setPending} action="Create" onConfirm={createGroup} disabled={!groupData.name || !groupData.ownerId}>
                <Button variant="Type.SECONDARY" onClick={() => onCloseModal(false)}>Cancel</Button>
            </BulkActionFooter>
        </>
    );
};
